import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';

import { useAppDispatch, useAppSelector } from '@core/hooks/appHooks';
import {
  clearUnsavedChanges,
  selectHasUnsavedChanges,
  selectUnsavedChangesMessage,
  setUnsavedChanges,
} from '@core/store/appSlice/appSlice';
import { store } from '@core/store/store';

interface UnsavedChangesContextType {
  setHasUnsavedChanges: (hasChanges: boolean, message?: string) => void;
  clearUnsavedChanges: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(
  null,
);

export const useUnsavedChangesContext = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      'useUnsavedChanges must be used within an UnsavedChangesProvider',
    );
  }
  return context;
};

interface UnsavedChangesProviderProps {
  children: ReactNode;
  defaultMessage?: string;
}

interface BlockerLocation {
  currentLocation: { pathname: string; search: string };
  nextLocation: { pathname: string; search: string };
}

export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({
  children,
  defaultMessage,
}) => {
  const dispatch = useAppDispatch();
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);
  const message = useAppSelector(selectUnsavedChangesMessage);

  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: BlockerLocation) => {
      const current = `${currentLocation.pathname}${currentLocation.search}`;
      const next = `${nextLocation.pathname}${nextLocation.search}`;
      const hasChanges = selectHasUnsavedChanges(store.getState());
      return hasChanges && current !== next;
    },
    [],
  );
  const blocker = useBlocker(shouldBlock);

  // Handle confirmation dialog
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const proceed = window.confirm(message || defaultMessage);
      if (proceed) {
        dispatch(clearUnsavedChanges());
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker.state, message, defaultMessage, dispatch, blocker]);

  // Handle browser/tab close/refresh
  useBeforeUnload(
    useCallback(
      event => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          event.returnValue = message;
        }
      },
      [hasUnsavedChanges, message, defaultMessage],
    ),
    { capture: true },
  );

  // Interface for components to interact with unsaved changes state
  const setHasUnsavedChanges = useCallback(
    (hasChanges: boolean, customMessage?: string) => {
      dispatch(
        setUnsavedChanges({
          hasChanges,
          message: customMessage,
        }),
      );
    },
    [dispatch],
  );

  const handleClearUnsavedChanges = useCallback(() => {
    dispatch(clearUnsavedChanges());
  }, [dispatch]);

  const value = {
    setHasUnsavedChanges: setHasUnsavedChanges,
    clearUnsavedChanges: handleClearUnsavedChanges,
  };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Test Scenarios
 * - Product details
 *    - No changes, navigate to another product
 *    - Changes, navigate to another product
 *    - No changes, navigate to another page
 *    - Changes, navigate to another page
 * - Product variants
 *    - No changes, navigate to another product
 *    - Changes, navigate to another product
 *    - No changes, navigate to another page
 *    - Changes, navigate to another page
 * - Other pages
 *    - No changes, navigate to another page
 */
